import {Box, Divider, Flex, Icon, IconWithText} from 'exsportia-components'
import React, {useCallback, useMemo, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {useDispatch} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'

import {RAIN_CHECK_QUERY_KEY} from '../../api/booking/get-booking-rain-check'
import {Participant} from '../../api/booking/types'
import {CreatedBookingsModal} from '../../components/created-bookings-modal/created-bookings-modal'
import {PricingDetails, PricingElementType, PricingPromo} from '../../components/pricing-details/pricing-details'
import {ParticipantsList} from '../../components/timeslot-grid/components/participants-list'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../constants/cancel-policy'
import {getParticipantsEquipment, getUsageString} from '../../helpers/data'
import {safeRound} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {BookingService} from '../../services/booking'
import {cancelBooking} from '../../services/booking/actions'
import {useServiceOptions} from '../../services/service-options/hooks'
import {Chip} from '../../ui/components/chip/chip'
import {useBookingCancelDetails} from '../../api/booking/get-booking-cancel-details-data'
import {LabeledSection} from '../../ui/components/labeled-section/labeled-section'
import {TextWithShowMore} from '../../ui/components/text-with-show-more/text-with-show-more'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {useLocale, useLuxon} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {ContactUsPage} from '../contact-us/contact-us-page'
import {AreaSection} from './components/area-section'
import {BookingSection} from './components/booking-section'
import {CancelBookingConfirmationModal} from './components/cancel-booking-confirmation-modal'
import {ClassInstructorsSection} from './components/class-instructors-section'
import {InstructorSection} from './components/instructor-section'
import {LocationSection} from './components/location-section'
import {SubscriptionSection} from './components/subscription-section'
import {RainCheckCard} from './rain-check/rain-check-card'

type AdditionalFeeOrDiscount = {
  price: number
  isDiscount: boolean
  label?: string
} | null

type PriceBlockType = {
  title: string | null
  titleStyleType: 'h3' | 'h4'
  items: PricingElementType[]
  additionalFeeOrDiscount?: AdditionalFeeOrDiscount
  promo?: PricingPromo
}

type Params = {
  id: string
  venueId: string
}
// eslint-disable-next-line max-lines-per-function
export const BookingProfilePage = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const {l} = useLocale()
  const navigate = useNavigate()
  const luxon = useLuxon()
  const params = useParams<Params>()
  const {openModal, closeModal} = useModalActions()
  const {isLoggedIn} = useAuthState()
  const [isCancelling, setIsCancelling] = useState(false)

  const {
    data: bookingRaw,
    // isLoading,
    isFetching: isLoading,
    refetch,
  } = useQuery(
    BookingService.queries.getBookingQuery({
      params: {id: params.id},
      query: {entity: 'booking', entityId: params.id || null},
      cacheTime: 10000,
      refetchOnWindowFocus: false,
    }),
  )

  // A multi-session group id resolves to an array of bookings (this is where the external 3DS
  // redirect lands). Land the user on the first booking, and for a real group (>1) also surface
  // the modal listing every created booking so they can open any of them. WBA-4103.
  const hasHandledGroupRef = React.useRef(false)
  React.useEffect(() => {
    if (!Array.isArray(bookingRaw) || bookingRaw.length === 0 || hasHandledGroupRef.current) {
      return
    }
    hasHandledGroupRef.current = true
    const group = bookingRaw as Record<string, string>[]
    const firstUid = group[0].uid || group[0].bookingId
    if (group.length > 1) {
      openModal({
        title: l('bookings'),
        footerConfig: {disabled: true},
        component: (
          <CreatedBookingsModal
            bookings={group.map((b) => ({
              bookingId: b.uid,
              title: b.activityName || b.name,
              subtitle: b.date,
            }))}
            onSelect={(id) => {
              closeModal()
              navigate(`/${params.venueId}/bookings/${id}`, {replace: true})
            }}
          />
        ),
      })
    }
    if (firstUid && params.venueId) {
      navigate(`/${params.venueId}/bookings/${firstUid}`, {replace: true})
    }
  }, [bookingRaw, navigate, params.venueId, openModal, closeModal, l])

  const booking = Array.isArray(bookingRaw) ? null : bookingRaw

  const {getServiceConfigByServiceType} = useServiceOptions([])
  const isJustTheSpace = booking?.areaSpace ?? false
  const profileType = booking?.entity === 'class' ? 'class' : isJustTheSpace ? 'justTheSpace' : 'service'
  const {label, icon} = useMemo(
    () => getServiceConfigByServiceType(booking?.sportType),
    [isJustTheSpace, booking, getServiceConfigByServiceType],
  )

  const handleOpenContactsModal = useCallback(
    () =>
      !booking
        ? null
        : openModal({
            title: l('contactUs'),
            // eslint-disable-next-line
            component: (
              <ContactUsPage
                venueId={booking.venueId || booking.venue?.uid || params.venueId || ''}
                booking={{reference: booking.reference, venueName: booking.venue?.name}}
              />
            ),
            footerConfig: {
              disabled: true,
            },
          }),
    [booking, l, openModal, params.venueId],
  )

  const cancelPolicyKey = booking?.cancelPolicy
    ? FULL_CANCEL_POLICY_OPTIONS_MAP[booking.cancelPolicy]?.description
    : undefined
  // A booking with no ladder step falls under the venue's own policy: the server names it in cancel-details.
  const needsVenuePolicy = Boolean(booking && !booking.cancelPolicy && !booking.isCancelled)
  const {data: cancelDetails} = useBookingCancelDetails(needsVenuePolicy ? booking?.uid ?? '' : '')
  const venuePolicy = cancelDetails?.venueCancelPolicy
  const cancelPolicyDescription =
    booking?.paymentType === null
      ? booking.cancelForPayOnLocationDesc
      : cancelPolicyKey
      ? l(cancelPolicyKey)
      : venuePolicy
      ? [venuePolicy.name, venuePolicy.customerSummary].filter(Boolean).join('. ')
      : booking?.cancelPolicy || '-'

  const header = useMemo(() => {
    return (
      <Flex
        p={'16px 16px 0px'}
        gap={'8px'}
        flexDirection={'column'}
      >
        <Chip
          styleType={profileType}
          text={l(label)}
          icon={icon}
        />
        <h2>{booking?.name ?? l('name')}</h2>
      </Flex>
    )
  }, [booking?.name, icon, profileType, l, label])

  const prices = useMemo(() => {
    if (!booking || !booking.participants?.length) {
      return []
    }

    const priceAndDiscount = booking.participants.reduce(
      (acc, participant) => ({
        discount: safeRound(acc.discount + (participant.totalDiscount ?? participant.discount ?? 0)),
        price: safeRound(acc.price + (participant.activityPrice ?? 0)),
        totalPrice: safeRound(acc.totalPrice + (participant.totalPrice ?? 0)),
        initialDiscount: safeRound(acc.initialDiscount + (participant.totalInitialDiscount ?? 0)),
        equipmentTotal: safeRound(acc.equipmentTotal + (participant.equipmentsTotalPrice ?? 0)),
      }),
      {price: 0, discount: 0, initialDiscount: 0, totalPrice: 0, equipmentTotal: 0},
    )

    // For class bookings, participant.activityPrice may be the edited price.
    // Use booking.price (original slot price × participants) as fallback.
    const bookingOriginalPrice = Number(booking.price ?? 0)
    const participantCount = booking.participants?.length || 1
    const correctedActivityPrice =
      bookingOriginalPrice > 0 && bookingOriginalPrice !== priceAndDiscount.price
        ? safeRound(bookingOriginalPrice * participantCount)
        : priceAndDiscount.price

    const totalLinesPrice = safeRound(
      correctedActivityPrice +
        priceAndDiscount.equipmentTotal -
        priceAndDiscount.discount -
        priceAndDiscount.initialDiscount,
    )

    const actualTotalPrice = safeRound(booking.totalPrice ?? 0)

    const subscriptionDiscount = booking.discount ?? 0
    let additionalFeeOrDiscount: AdditionalFeeOrDiscount = null

    if (booking.additionalFeeOrDiscount !== undefined) {
      // Server computes it from the raw booking doc — same source as VMS and emails.
      additionalFeeOrDiscount = booking.additionalFeeOrDiscount
    } else if (subscriptionDiscount === 0 && totalLinesPrice !== actualTotalPrice) {
      const priceDifference = Math.abs(actualTotalPrice - totalLinesPrice)
      const isTotalPriceLess = totalLinesPrice > actualTotalPrice
      additionalFeeOrDiscount = {
        price: priceDifference,
        isDiscount: isTotalPriceLess,
      }
    }

    const promoDiscount = safeRound(Number(booking.promoDiscount || 0))
    const promo: PricingPromo =
      booking.promoCode && promoDiscount > 0 ? {code: booking.promoCode, amount: promoDiscount} : null

    const readyPrices: PriceBlockType[] = [
      {
        title: null,
        titleStyleType: 'h3',
        items: [
          {
            count: booking.participants.length,
            price: additionalFeeOrDiscount ? correctedActivityPrice : priceAndDiscount.price,
            discount: priceAndDiscount.discount,
            initialDiscount: priceAndDiscount.initialDiscount,
            totalPrice: priceAndDiscount.totalPrice,
            text: booking.name || '',
          },
          ...getParticipantsEquipment(booking.participants),
        ],
        additionalFeeOrDiscount,
        promo,
      },
    ]

    if (booking?.splitPayment) {
      const paidParticipantsItems: PricingElementType[] = []
      const unpaidParticipantsItems: PricingElementType[] = []
      booking.participants.forEach((participant, index) => {
        const item = {
          count: 0,
          discount: 0,
          totalPrice: participant.totalPrice ?? 0,
          subtext: (
            <Flex alignItems='center'>
              {participant.name}
              <Divider styleType='dot' />
              {participant.membershipName ? (
                <Flex maxWidth={['200px', '400px']}>
                  <IconWithText
                    icon='crownAction'
                    text={participant.membershipName}
                    settings={{icon: {color: '#FF9E00'}, text: {color: '#FF9E00'}}}
                  />
                </Flex>
              ) : null}
            </Flex>
          ),
          price: participant.totalPrice ?? 0,
          text: (
            <Flex>
              {index + 1}/{booking.participants.length}
              <Icon icon='user' /> {booking.activityName}
            </Flex>
          ),
        }
        if (participant.isPaid) {
          paidParticipantsItems.push(item)
        } else {
          unpaidParticipantsItems.push(item)
        }
      })

      const paidParticipantsPrices: PriceBlockType = {
        title: 'PaidSoFar',
        titleStyleType: 'h4',
        items: paidParticipantsItems,
      }
      readyPrices.push(paidParticipantsPrices)
      if (unpaidParticipantsItems.length) {
        const unpaidParticipantsPrices: PriceBlockType = {
          titleStyleType: 'h4',
          title: 'RemainingBalance',
          items: unpaidParticipantsItems,
        }
        readyPrices.push(unpaidParticipantsPrices)
      }
    }
    return readyPrices
  }, [booking])

  const bookingDateTime = booking?.timestamp ? luxon.fromMillis(Number(booking.timestamp)) : luxon.now()
  const bookingDuration = l(booking?.duration === 'NO_DURATION' ? 'noDuration' : 'minutes_short', {
    value: booking?.duration ?? '-',
  })
  const withEquipments = booking?.participants?.some((el) => isNotNilAndNotEmpty(el.equipments)) ?? false
  const paymentStatus = booking?.isCancelled
    ? 'cancelled'
    : booking?.isPaid
    ? 'paid'
    : booking?.splitPayment
    ? 'partiallyPaid'
    : 'unpaid'

  const openParticipantsModal = useCallback(() => {
    openModal({
      title: l('participants'),
      footerConfig: {disabled: true},
      component: (
        <ParticipantsList
          participants={booking?.participants || []}
          displayEquipmentCount={true}
        />
      ),
    })
  }, [booking?.participants, openModal])

  const onCancelConfirm = useCallback(() => {
    if (!booking) {
      return null
    }
    setIsCancelling(true)
    dispatch(
      cancelBooking({
        isPaid: booking.isPaid,
        bookingId: booking.uid,
        l,
        callback: () => {
          setIsCancelling(false)
          refetch()
          // The Rain check card then shows what happened (claimed). Nothing else to do for it.
          queryClient.invalidateQueries(RAIN_CHECK_QUERY_KEY)
        },
        rejectCallback: () => setIsCancelling(false),
      }),
    )
    closeModal()
  }, [booking, queryClient])

  const handleCancelBooking = useCallback(() => {
    if (!booking) {
      return null
    }
    openModal({
      title: l('bookingCancellation', {caseAction: 'capitalize'}),
      component: (
        <CancelBookingConfirmationModal
          bookingId={booking.uid}
          onCancelConfirm={onCancelConfirm}
          isLoggedIn={isLoggedIn}
        />
      ),
      footerConfig: {disabled: true},
    })
  }, [booking, onCancelConfirm, isLoggedIn])
  const handlePay = useCallback(() => {
    if (booking?.splitPayment) {
      navigate(`/${params.venueId}/bookings/${booking.uid}/pay`)
    } else {
      navigate(`/clear-view/${params.venueId}/payment?entityId=${params.id}&entity=booking_update`)
    }
  }, [navigate, params.venueId, params.id, booking])

  const aboutChildrenArray = useMemo(() => {
    if (isLoading) {
      return null
    }
    const arrayToReturn = []
    if (booking?.areaId) {
      arrayToReturn.push(<AreaSection areaId={booking.areaId} />)
    }
    if (booking?.teacherId) {
      arrayToReturn.push(<InstructorSection teacherId={booking.teacherId} />)
    }
    if (isNotNilAndNotEmpty(booking?.teachers)) {
      // eslint-disable-next-line
      arrayToReturn.push(<ClassInstructorsSection instructors={booking!.teachers} />)
    }
    const firstParticipant: (Participant & {membership?: {logo: {secure_url: string}; name: string}}) | undefined =
      booking?.participants[0]
    if (firstParticipant?.membership) {
      arrayToReturn.push(
        <SubscriptionSection
          firstParticipant={firstParticipant}
          isLoading={isLoading}
          usageData={booking?.usageData}
        />,
      )
    }
    return arrayToReturn
  }, [isLoading, booking])

  return (
    <GridLayout header={header}>
      <Box>
        <TitledSection title={l('about')}>{aboutChildrenArray}</TitledSection>
        <TitledSection title={l('details')}>
          {booking?.description && (
            <LabeledSection label={l('description')}>
              <TextWithShowMore text={booking.description} />
            </LabeledSection>
          )}
          <LabeledSection label={l('additionalInformation')}>
            <TextWithShowMore text={booking?.arrivalInstructions ?? '-'} />
          </LabeledSection>
          <LabeledSection label={l('specialRequest')}>
            {booking?.specialRequests ? booking.specialRequests : '-'}
          </LabeledSection>
          <LabeledSection label={l('labels_cancelPolicy')}>{cancelPolicyDescription || '-'}</LabeledSection>
        </TitledSection>
        {(booking?.area?.address || booking?.place?.address || booking?.venue?.address) && (
          <TitledSection title={l('location')}>
            <LocationSection address={(booking.area?.address || booking.place?.address || booking.venue?.address)!} />
          </TitledSection>
        )}
        <Flex flexDirection='column'>
          {prices.map((el, index) => (
            <React.Fragment key={index}>
              {index !== 0 && <Divider settings={{m: '16px 0'}} />}
              <PricingDetails
                key={index}
                title={el.title}
                values={el.items}
                isLoading={isLoading}
                titleStyleType={el.titleStyleType}
                currency={booking?.venue.currency}
                total={
                  el.additionalFeeOrDiscount || el.promo
                    ? booking?.totalPrice ?? el.items.reduce((acc, item) => acc + (item.totalPrice ?? 0), 0)
                    : el.items.reduce((acc, item) => acc + (item.totalPrice ?? 0), 0)
                }
                discount={el.items.reduce((acc, item) => acc + (item.discount || 0), 0)}
                initialDiscount={el.items.reduce((acc, item) => acc + (item.initialDiscount || 0), 0)}
                additionalFeeOrDiscount={el.additionalFeeOrDiscount}
                promo={el.promo}
              />
            </React.Fragment>
          ))}
        </Flex>
      </Box>
      {booking && (
        <BookingSection
          booking={booking}
          isLoading={isLoading}
          handlePay={handlePay}
          paymentStatus={paymentStatus}
          withEquipments={withEquipments}
          getUsageString={getUsageString}
          bookingDuration={bookingDuration}
          bookingDateTime={bookingDateTime}
          isBookingLoading={isCancelling || isLoading}
          handleCancelBooking={handleCancelBooking}
          openParticipantsModal={openParticipantsModal}
          handleOpenContactsModal={handleOpenContactsModal}
          aboveCancel={
            <RainCheckCard
              bookingId={booking.uid}
              isLoggedIn={isLoggedIn}
              venueTimeZone={booking.venue?.timeZone}
              currency={booking.venue?.currency ?? booking.currency}
              bookingName={booking.name}
              bookingWhen={bookingDateTime.toUTC().toFormat('MMM dd, HH:mm')}
              isBusy={isCancelling || isLoading}
              onCancel={handleCancelBooking}
              onContactVenue={handleOpenContactsModal}
            />
          }
        />
      )}
    </GridLayout>
  )
}
